import * as React from 'react';
import { cn } from '../../lib/utils';

interface ProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  value: number;
  max?: number;
  showLabel?: boolean;
  variant?: 'default' | 'success' | 'warning' | 'danger';
}

const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(
  ({ className, value, max = 100, showLabel = false, variant = 'default', ...props }, ref) => {
    const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
    
    // Auto-determine variant based on percentage if default
    const computedVariant = variant === 'default' 
      ? percentage >= 100 
        ? 'danger' 
        : percentage >= 90 
          ? 'warning' 
          : 'success'
      : variant;

    const barColors = {
      default: 'bg-primary',
      success: 'bg-green-500',
      warning: 'bg-yellow-500',
      danger: 'bg-red-500',
    };

    return (
      <div className={cn('flex items-center gap-2', className)} {...props} ref={ref}>
        <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary">
          <div
            className={cn(
              'h-full transition-all duration-300 ease-in-out',
              barColors[computedVariant]
            )}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
        {showLabel && (
          <span className={cn(
            'text-xs font-medium min-w-[40px] text-right',
            computedVariant === 'danger' && 'text-red-600',
            computedVariant === 'warning' && 'text-yellow-600',
          )}>
            {percentage.toFixed(0)}%
          </span>
        )}
      </div>
    );
  }
);
Progress.displayName = 'Progress';

export { Progress };
